import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Tab,
    Tabs,
    TextField,
    Typography,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    TableBody,
    CircularProgress,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { RiEditCircleFill } from "react-icons/ri";
import { FiSearch } from "react-icons/fi";
import Layout from "../../components/Layout/Layout";
import { getAppVersions, createAppVersion, updateAppVersion } from "../../API/VersionsAPI/VersionsAPI";

const StyledTableCell = styled(TableCell)(() => ({
    fontWeight: 600,
    color: "#2d2d2d",
    fontSize: "13px",
    borderBottom: "1px solid #e0e0e0",
}));

const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(even)": {
        backgroundColor: "#f9f9f9",
    },
}));

export default function AppVersions() {
    const [filterPlatform, setFilterPlatform] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editVersionId, setEditVersionId] = useState(null);
    const [loading, setIsLoading] = useState(false);
    const [versionsList, setVersionsList] = useState([]);
    const [refresh, setRefresh] = useState(true);

    const [formData, setFormData] = useState({
        versionName: "",
        versionCode: "",
        platform: "android",
        isMandatoryUpdate: false,
        releaseNotes: "",
        isActive: true,
    });

    useEffect(() => {
        const token = sessionStorage.getItem('token');
        if (token) {
            setIsLoading(true);
            const isActiveParam = filterStatus === 'All' ? 'All'
                : (filterStatus === 'Active' ? true : false);

            getAppVersions(token, filterPlatform, isActiveParam)
                .then((res) => {
                    if (res?.data?.success) {
                        setVersionsList(res?.data?.data || []);
                    }
                    setIsLoading(false);
                })
                .catch((err) => {
                    console.error(err);
                    setIsLoading(false);
                });
        }
    }, [refresh, filterPlatform, filterStatus]);

    const handleOpenModal = () => {
        setIsEditing(false);
        setFormData({
            versionName: "",
            versionCode: "",
            platform: "android",
            isMandatoryUpdate: false,
            releaseNotes: "",
            isActive: true,
        });
        setEditVersionId(null);
        setIsModalOpen(true);
    };

    const handleEdit = (version) => {
        setIsEditing(true);
        setFormData({
            versionName: version.versionName,
            versionCode: version.versionCode,
            platform: version.platform,
            isMandatoryUpdate: version.isMandatoryUpdate,
            releaseNotes: version.releaseNotes || "",
            isActive: version.isActive,
        });
        setEditVersionId(version._id);
        setIsModalOpen(true);
    };

    const handleSave = () => {
        if (!formData.versionName || !formData.versionCode || !formData.platform) {
            alert("Please fill in the required fields (Version Name, Version Code, Platform).");
            return;
        }

        const payload = {
            ...formData,
            versionCode: Number(formData.versionCode)
        };

        const token = sessionStorage.getItem('token');
        if (!token) return;

        if (isEditing && editVersionId) {
            updateAppVersion(token, editVersionId, payload)
                .then((res) => {
                    setIsModalOpen(false);
                    alert('App Version Updated Successfully!');
                    setRefresh(!refresh);
                })
                .catch((err) => {
                    console.error(err);
                    alert('Error updating App Version.');
                });
        } else {
            createAppVersion(token, payload)
                .then((res) => {
                    setIsModalOpen(false);
                    alert('App Version Created Successfully!');
                    setRefresh(!refresh);
                })
                .catch((err) => {
                    console.error(err);
                    alert('Error creating App Version.');
                });
        }
    };

    const getStatusStyle = (status) => {
        return status ? { backgroundColor: "#4CAF50", color: "#fff" } : { backgroundColor: "#9E9E9E", color: "#fff" };
    };

    return (
        <Layout>
            <Box sx={{ minHeight: "100vh", p: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    App Versions
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666", mb: 3 }}>
                    Manage and track mobile application builds across different platforms.
                </Typography>

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <TextField
                            select
                            label="Platform"
                            variant="outlined"
                            size="small"
                            value={filterPlatform}
                            onChange={(e) => setFilterPlatform(e.target.value)}
                            sx={{ width: 150 }}
                        >
                            <MenuItem value="All">All Platforms</MenuItem>
                            <MenuItem value="android">Android</MenuItem>
                            <MenuItem value="ios">iOS</MenuItem>
                        </TextField>

                        <TextField
                            select
                            label="Status"
                            variant="outlined"
                            size="small"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            sx={{ width: 150 }}
                        >
                            <MenuItem value="All">All Status</MenuItem>
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Inactive">Inactive</MenuItem>
                        </TextField>
                    </Box>

                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#1976d2",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 1.5,
                            height: 40,
                            px: 2.5,
                        }}
                        onClick={handleOpenModal}
                    >
                        Create New Version
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "40vh" }}>
                        <CircularProgress sx={{ color: "#1976d2" }} />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0px 2px 8px rgba(0,0,0,0.1)" }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Version Name</StyledTableCell>
                                    <StyledTableCell>Version Code</StyledTableCell>
                                    <StyledTableCell>Platform</StyledTableCell>
                                    <StyledTableCell>Mandatory Update</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Created At</StyledTableCell>
                                    <StyledTableCell>Updated At</StyledTableCell>
                                    <StyledTableCell align="center">Actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {versionsList.map((row) => (
                                    <StyledTableRow key={row._id}>
                                        <TableCell>{row.versionName}</TableCell>
                                        <TableCell>{row.versionCode}</TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{row.platform}</TableCell>
                                        <TableCell>{row.isMandatoryUpdate ? "Yes" : "No"}</TableCell>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: "inline-block",
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    ...getStatusStyle(row.isActive),
                                                }}
                                            >
                                                {row.isActive ? "Active" : "Inactive"}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
                                        </TableCell>
                                        <TableCell>
                                            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "-"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <RiEditCircleFill
                                                style={{ color: "#EEB50C", fontSize: "1.4vw", cursor: "pointer" }}
                                                onClick={() => handleEdit(row)}
                                            />
                                        </TableCell>
                                    </StyledTableRow>
                                ))}
                                {versionsList.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            No app versions found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Create / Edit Dialog */}
                <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 600 }}>
                        {isEditing ? "Edit App Version" : "Create New App Version"}
                    </DialogTitle>
                    <DialogContent>
                        <Box mt={1} display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Version Name"
                                fullWidth
                                size="small"
                                value={formData.versionName}
                                onChange={(e) => setFormData({ ...formData, versionName: e.target.value })}
                                placeholder="e.g. 1.0.1"
                            />
                            <TextField
                                label="Version Code"
                                type="number"
                                fullWidth
                                size="small"
                                value={formData.versionCode}
                                onChange={(e) => setFormData({ ...formData, versionCode: e.target.value })}
                                placeholder="e.g. 1"
                            />
                            <FormControl size="small" fullWidth>
                                <InputLabel>Platform</InputLabel>
                                <Select
                                    value={formData.platform}
                                    label="Platform"
                                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                                >
                                    <MenuItem value="android">Android</MenuItem>
                                    <MenuItem value="ios">iOS</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Release Notes"
                                multiline
                                rows={3}
                                fullWidth
                                size="small"
                                value={formData.releaseNotes}
                                onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isMandatoryUpdate}
                                        onChange={(e) => setFormData({ ...formData, isMandatoryUpdate: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label="Mandatory Update"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        color="success"
                                    />
                                }
                                label="Active Version"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setIsModalOpen(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSave} color="primary">
                            {isEditing ? "Update" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Layout>
    );
}
